/**
 * BasicModalShell
 *
 * Shared modal shell used across the app.
 * Goals:
 * - One place to control mobile vs desktop modal behavior (full-height on mobile, fixed dialog on sm+)
 * - Consistent backdrop + animation + close button hit area + scroll behavior
 * - Allow per-modal customization of sizing/close placement without copy/paste
 */
"use client";

import { type ReactNode, useEffect, useId, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";

import { ModalCloseButton } from "@/components/shared/ModalCloseButton";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { cn } from "@/lib/cn";
import { tailwindBreakpoints } from "@/styles/design-tokens";

export interface BasicModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Rendered above the scroll container (useful for progress bars / pinned headers). */
  topSlot?: ReactNode;
  /**
   * Called after the exit animation completes (i.e. when the modal has fully disappeared).
   * Useful for parent-controlled unmounting without hardcoded timeouts.
   */
  onAfterClose?: () => void;

  /** If true, clicking the shaded backdrop calls onClose. */
  closeOnBackdropClick?: boolean;

  /** Modal z-index wrapper; defaults match existing modals. */
  zIndexClassName?: string;

  /** Override positioning/padding for the overlay wrapper. */
  overlayClassName?: string;

  /** Extra classes for the modal panel itself. */
  panelClassName?: string;

  /** Extra classes for the scroll container that wraps children. */
  scrollClassName?: string;

  /** Controls whether the close button is shown. */
  showCloseButton?: boolean;

  /** Customize close button placement/sizing. */
  closeButtonClassName?: string;
}

export function BasicModalShell({
  isOpen,
  onClose,
  children,
  topSlot,
  onAfterClose,
  closeOnBackdropClick = true,
  zIndexClassName,
  overlayClassName,
  panelClassName,
  scrollClassName,
  showCloseButton = true,
  closeButtonClassName,
}: BasicModalShellProps) {
  const z = zIndexClassName ?? "z-[110]";
  const lockKey = useId();
  const [mounted, setMounted] = useState(false);

  // Lock body scroll when modal is open (desktop only).
  // On mobile Safari, `body { overflow: hidden }` can break scrolling inside fixed-position modals.
  useBodyScrollLock({ key: lockKey, enabled: isOpen, minWidthPx: tailwindBreakpoints.sm });

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence onExitComplete={onAfterClose}>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeOnBackdropClick ? onClose : undefined}
            className={cn("fixed inset-0 bg-black/20 backdrop-blur-sm", z)}
          />

          <div
            className={cn(
              "fixed inset-0 flex items-center justify-center sm:p-4",
              z,
              overlayClassName
            )}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              className={cn(
                "relative w-full overflow-hidden bg-white h-[100svh] rounded-none sm:h-auto sm:max-h-[90vh] sm:max-w-[636px] sm:rounded-[8.75px] flex flex-col",
                panelClassName
              )}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              {showCloseButton && (
                <ModalCloseButton
                  onClick={onClose}
                  className={cn(
                    "absolute right-4 top-4 z-20 sm:right-8 sm:top-[29px]",
                    closeButtonClassName
                  )}
                />
              )}

              {topSlot}

              <div
                className={cn(
                  "flex-1 overflow-y-auto overscroll-contain flex items-center",
                  scrollClassName
                )}
              >
                <div className="w-full">{children}</div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
